import React from 'react';
import yaml from 'js-yaml';
// renderers is imported separately so Markdoc isn't sent to the client
import Markdoc, {renderers} from '@markdoc/markdoc'

import {getSchema} from '/opt/buildhome/repo/node_modules/@markdoc/next.js/src/runtime.js';
/**
 * Schema is imported like this so end-user's code is compiled using build-in babel/webpack configs.
 * This enables typescript/ESnext support
 */
const config = {};
import * as tags from '/opt/buildhome/repo/markdoc/tags.js'
import * as nodes from '/opt/buildhome/repo/markdoc/nodes.js'
const functions = {};
const schema = {
tags: tags ? (tags.default || tags) : {},
nodes: nodes ? (nodes.default || nodes) : {},
functions: functions ? (functions.default || functions) : {},
...(config ? (config.default || config) : {}),
};

/**
 * Source will never change at runtime, so parse happens at the file root
 */
const source = "---\ntitle: Mendable Vanilla JS\ndescription: Using Mendable in Vanilla JS.\n---\n\nBefore install, make sure you have either signed up through our [platform](https://mendable.ai) or we have provided you with the `ANON_KEY`. Without the key, you won't be able to setup the search component.\n\n---\n\n## Mendable Vanilla JS\n\nTo use Mendable with Vanilla JS, you need to include a script tag in your HTML file.\n\n### Floating Button\n\nFor the floating button it is pretty straightfoward. Just add these 2 scripts tags to the your HTML.\n\nFor customization refer to the [props](/components/floating-button#props) section of the floating button component.\n\n```html\n<script src=\"https://unpkg.com/@mendable/search@0.0.155/dist/umd/mendable-bundle.min.js\"></script>\n<script>\n  Mendable.initialize({\n    anon_key: 'YOUR_ANON_KEY',\n    type: 'floatingButton',\n    // all the other props for the component type\n  })\n</script>\n```\n\nNote: The `botIcon`, `userIcon` and `icon` props should be passed as a public image URL string and not as an HTML tag.\n\n### Search Bar\n\nFor the search bar, you need to add a div with an id to your HTML file. Then add these 2 scripts tags to the your HTML.\n\nThe div represents where your component will be rendered in the page. You can change the id to whatever you want, but make sure to change the `elementId` prop to match the id of the div.\n\nFor customization refer to the [props](/components/search-bar#props) section of the search bar component.\n\n```html\n<div id=\"mendable-component\"></div>\n\n<script src=\"https://unpkg.com/@mendable/search@0.0.155/dist/umd/mendable-bundle.min.js\"></script>\n<script>\n  Mendable.initialize({\n    anon_key: 'YOUR_ANON_KEY',\n    type: 'searchBar',\n    elementId: 'mendable-component', // required\n    // all the other props for the component type\n  })\n</script>\n```\n\n### MendableInPlace\n\nThis component is the Mendable Chat only, does not include an activation component or dialog transitions, making it easy for you to create your own activations on top of it.\n\nFor customization refer to the [props](/components/in-place#props) section of the search bar component.\n\n```html\n<div id=\"mendable-component\"></div>\n\n<script src=\"https://unpkg.com/@mendable/search@0.0.155/dist/umd/mendable-bundle.min.js\"></script>\n<script>\n  Mendable.initialize({\n    anon_key: 'YOUR_ANON_KEY',\n    type: 'inPlace',\n  })\n</script>\n```\n\n### Chat Bubble\n\nThe Chat Bubble is a component that appears as a floating chat icon, typically pinned to the bottom right of the screen. When clicked, it will open a dialog with Mendable Chat.\n\n```html\n<div id=\"mendable-component\"></div>\n\n<script src=\"https://unpkg.com/@mendable/search@0.0.155/dist/umd/mendable-bundle.min.js\"></script>\n<script>\n  Mendable.initialize({\n    anon_key: 'YOUR_ANON_KEY',\n    type: 'chat',\n  })\n</script>\n```\n";
const filepath = "/components/vanilla-js.md";
const ast = Markdoc.parse(source);

/**
 * Like the AST, frontmatter won't change at runtime, so it is loaded at file root.
 * This unblocks future features, such a per-page dataFetchingFunction.
 */
const frontmatter = ast.attributes.frontmatter
  ? yaml.load(ast.attributes.frontmatter)
  : {};

const {components, ...rest} = getSchema(schema)

export async function getStaticProps(context) {
  const partials = {};

  // Ensure Node.transformChildren is available
  Object.keys(partials).forEach((key) => {
    partials[key] = Markdoc.parse(partials[key]);
  });

  const cfg = {
    ...rest,
    variables: {
      ...(rest ? rest.variables : {}),
      // user can't override this namespace
      markdoc: {frontmatter},
      // Allows users to eject from Markdoc rendering and pass in dynamic variables via getServerSideProps
      ...(context.variables || {})
    },
    partials,
    source,
  };

  /**
   * transform must be called in dataFetchingFunction to support server-side rendering while
   * accessing variables on the server
   */
  const content = Markdoc.transform(ast, cfg);

  return {
    // Removes undefined
    props: JSON.parse(
      JSON.stringify({
        markdoc: {
          content,
          frontmatter,
          file: {
            path: filepath
          }
        },
      })
    ),
  };
}

export default function MarkdocComponent(props) {
  // Only execute HMR code in development
  return renderers.react(props.markdoc.content, React, {
    components: {
      ...components,
      // Allows users to override default components at runtime, via their _app
      ...props.components,
    },
  });
}
